import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRecords, apiDeleteRecord, apiUpdateRecord, clearToken } from '../api.js';
import RecordForm from '../components/RecordForm.jsx';
import ChangePassword from '../components/ChangePassword.jsx';
import { Search, Lock, Plus, SquarePen, Trash2 } from '../icons.jsx';

const LIMIT = 10;

export default function Admin() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState(null); // null | 'new' | record
  const [pwOpen, setPwOpen] = useState(false);
  const [toast, setToast] = useState('');

  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRecords({ q: q || undefined, page, limit: LIMIT });
      setRecords(data.data || []);
      setTotal(data.pagination?.total || 0);
    } catch {
      flash('无法连接到服务器');
    } finally {
      setLoading(false);
    }
  }, [q, page]);

  useEffect(() => { load(); }, [load]);
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const logout = () => { clearToken(); navigate('/login', { replace: true }); };

  const toggleStatus = async (r) => {
    const newStatus = Number(r.Status) === 1 ? 0 : 1;
    try {
      await apiUpdateRecord(r.Id, { ...r, Status: newStatus });
      setRecords((rs) => rs.map((x) => (x.Id === r.Id ? { ...x, Status: newStatus } : x)));
    } catch {
      flash('状态更新失败');
    }
  };

  const del = async (r) => {
    if (!window.confirm('确定要删除这条记录吗？')) return;
    try { await apiDeleteRecord(r.Id); load(); } catch { flash('删除出错'); }
  };

  const Th = ({ children, cls = '' }) => (
    <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${cls}`}>{children}</th>
  );
  const PageBtn = ({ disabled, children, onClick }) => (
    <button disabled={disabled} onClick={onClick} className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>{children}</button>
  );

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-gray-800">款式管理后台</h1>
          <div className="flex gap-4">
            <button onClick={() => setPwOpen(true)} className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"><Lock w={18} h={18} />修改密码</button>
            <button onClick={logout} className="text-red-600 hover:text-red-800 transition-colors">退出登录</button>
          </div>
        </div>

        {/* toolbar */}
        <div className="bg-white p-4 rounded-lg shadow mb-6 flex gap-4">
          <div className="flex-grow relative">
            <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="搜索款号或名称..." type="text" className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none" />
            <Search w={24} h={24} className="lucide-search absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
          </div>
          <button onClick={load} className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-2 rounded-lg font-medium">搜索</button>
          <button onClick={() => setFormState('new')} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium flex items-center gap-2"><Plus w={20} h={20} />新增款式</button>
        </div>

        {toast && <div className="mb-4 bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm">{toast}</div>}

        {/* table */}
        <div className="bg-white rounded-lg shadow overflow-hidden overflow-x-auto mb-6">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th cls="w-20">图片</Th>
                <Th cls="w-24">款号</Th>
                <Th cls="w-48">名称</Th>
                <Th cls="w-48">标签</Th>
                <Th cls="w-24">状态</Th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">操作</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && <tr><td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-400">加载中…</td></tr>}
              {!loading && records.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-400">暂无数据</td></tr>}
              {records.map((r) => (
                <tr key={r.Id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {r.ImageUrl ? <img alt="" src={r.ImageUrl} className="h-12 w-12 object-cover rounded bg-gray-100" /> : <div className="h-12 w-12 rounded bg-gray-100" />}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">{r.Code}</td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 max-w-xs truncate" title={r.Name}>{r.Name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={r.Tags}>{r.Tags}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button role="switch" aria-checked={Number(r.Status) === 1} onClick={() => toggleStatus(r)} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${Number(r.Status) === 1 ? 'bg-blue-600' : 'bg-gray-200'}`}>
                      <span aria-hidden="true" className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${Number(r.Status) === 1 ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                    <span className="ml-2 text-xs text-gray-500">{Number(r.Status) === 1 ? '显示' : '隐藏'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button onClick={() => setFormState(r)} className="text-blue-600 hover:text-blue-900 mr-4"><SquarePen w={18} h={18} /></button>
                    <button onClick={() => del(r)} className="text-red-600 hover:text-red-900"><Trash2 w={18} h={18} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* pagination */}
        <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">第 {page} 页 / 共 {totalPages} 页</div>
          <div className="flex gap-2">
            <PageBtn disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>上一页</PageBtn>
            <PageBtn disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>下一页</PageBtn>
          </div>
        </div>
      </div>

      {formState && <RecordForm record={formState === 'new' ? null : formState} onClose={() => setFormState(null)} onSaved={() => { setFormState(null); load(); }} />}
      {pwOpen && <ChangePassword onClose={() => setPwOpen(false)} />}
    </div>
  );
}
